import React, { useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { Controller } from "react-hook-form";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "../../data/modal";
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid,
  FormControl,
  Typography,
  LinearProgress,
} from "@mui/material";

export const ContainerUploadField: React.FC<any> = ({
  name,
  control,
  register,
  height = "10vh",
  callback,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<number>();
  const dispatch = useDispatch();
  const existingUploads = useSelector((s: any) => s.containerUploads.data);

  function parseEdifactDate(edifactString) {
    if (!/^\d{10}$/.test(edifactString)) return null;

    const yy = edifactString.slice(0, 2);
    const mm = edifactString.slice(2, 4);
    const dd = edifactString.slice(4, 6);
    const hh = edifactString.slice(6, 8);
    const mi = edifactString.slice(8, 10);

    const fullYear = parseInt(yy, 10) < 50 ? '20' + yy : '19' + yy;

    return `${fullYear}-${mm}-${dd}T${hh}:${mi}:00`;
  }

  const fileDropHandler = (accepted: File[]) => {
    const file = accepted[0];

    if (!file) return;

    const nameConflict = existingUploads.some(
      (u: any) => u.fileName === file.name
    );

    setIsLoading(true);
    setProgress(0);

    const totalDuration = 2000;
    const stepCount = 100;
    const stepTime = totalDuration / stepCount;
    let current = 0;
    intervalRef.current = window.setInterval(() => {
      current = Math.min(current + 1, 100);
      setProgress(current);
      if (current === 100 && intervalRef.current) clearInterval(intervalRef.current);
    }, stepTime);

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setProgress(100);
      if (file.type === "text/plain") {
        const content = evt.target?.result;
        const segments = content.split("'");
        const containers = {};
        // *   - SGP → containerID
        // *   - EQD → equipmentType (e.g. 45P1 or 45G1)
        // *   - MEA (gross weight after EQD) → grossWeightKg
        // *   - MEA (tare weight under EQD) → tareWeightKg
        // *   - MEA (cargo weight from the earlier MEA G line) → cargoWeightKg
        // *   - DIM+7, DIM+8, DIM+9 → length/width/height in cm
        // *   - RFF+ABO → bookingReference
        // *   - NAD+PW → pickupLocation
        // *   - DTM+CPA → pickupDateTime (converted to “YYYY-MM-DDTHH:MM:SS”)
        // *   - NAD+DP → deliveryLocation
        // *   - DTM+CPD → deliveryDateTime (converted to “YYYY-MM-DDTHH:MM:SS”)

        let currentGID = null;

        for (const segment of segments) {
          const parts = segment.trim().split('+');
          switch (parts[0]) {
            case 'GID':
              currentGID = parts[1];
              break;

            case 'SGP':
              if (parts[1]) {
                containers[parts[1]] = {
                  containerNumber: parts[1],
                  gid: currentGID,
                };
              }
              break;

            case 'MEA':
              if (parts[1] === 'AAE' && parts[2] === 'G' && currentGID) {
                const cont = Object.values(containers).find(c => c.gid === currentGID);
                if (cont) cont.grossWeight = parts[3]?.split(':')[1];
              }
              break;
          }
        }

        let currentContainer = null;

        for (const segment of segments) {
          const parts = segment.trim().split('+');

          switch (parts[0]) {
            case 'EQD':
              const containerNumber = parts[2] ? parts[2] : crypto.randomUUID();
              containers[containerNumber] = {
                ...containers[containerNumber], containerNumber: containerNumber
              };

              currentContainer = containers[containerNumber]

              if (!currentContainer) break;

              currentContainer.type = parts[3];
              break;

            case 'MEA':
              if (!currentContainer) break;
              if (parts[1] === 'AAE') {
                const meaType = parts[2];
                const value = parts[3]?.split(':')[1];
                if (meaType === 'T') currentContainer.tareWeight = value;
                if (meaType === 'G') currentContainer.grossWeight = value;
              }
              break;

            case 'DIM':
              if (!currentContainer) break;
              const dimCode = parts[1];
              if (dimCode === '7') currentContainer.length = parts[2]?.split('::')[1];
              if (dimCode === '8') currentContainer.width = parts[2]?.split('::')[1];
              if (dimCode === '9') currentContainer.height = parts[2]?.split(':::')[1];
              break;

            case 'RFF':
              if (!currentContainer) break;
              if (parts[1]?.startsWith('ABO')) {
                currentContainer.reference = parts[1].split(':')[1] || parts[2];
              }
              break;

            case 'NAD':
              if (!currentContainer) break;
              if (parts[1], parts[2]) {
                if (parts[1] === 'PW') {
                  currentContainer.from = parts[2];
                }
                if (parts[1] === 'DP') {
                  currentContainer.to = parts[2];
                }
              }
              break;

            case 'DTM':
              if (!currentContainer) break;

              if (parts[1]) {
                const type = parts[1]?.split(':')[0];
                const dateString = parts[1]?.split(':')[1];
                if (type === 'CPA') {
                  const date = parseEdifactDate(dateString)
                  currentContainer.pickupDateTime = date;
                }
                if (type === 'CPD') {
                  const date = parseEdifactDate(dateString)
                  currentContainer.deliveryDateTime = date;
                }
              }
              if (currentContainer.containerNumber === "temporaryReference" && currentContainer.reference) {
                containers[currentContainer.reference] = { ...currentContainer, containerNumber: currentContainer.reference }
                containers[currentContainer.containerNumber] = {}
              }
              break;
          }
        }

        const output = Object.values(containers)
        //loadingTerminals
        const distinctCol0 = Array.from(new Set(output.map(r => r.from)));
        //unloadingTerminals
        const distinctCol1 = Array.from(new Set(output.map(r => r.to)));
        //types
        const distinctCol5 = Array.from(new Set(output.map(r => r.type)));
        const grouped: Record<string, any[][]> = {};

        output.forEach(row => {
          const key = `${row.from}_${row.to}`;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(row);
        });

        if (output) {
          setTimeout(() => {
            dispatch(
              showModal({
                title: "Containers uploaded",
                description: "Uploaded containers by file",
                form: "containerupload",
                formProps: {
                  file: file?.path,
                  uploadedContainers: grouped,
                  loadingTerminals: distinctCol0,
                  unloadingTerminals: distinctCol1,
                  types: distinctCol5,
                  hasWarning: nameConflict,
                  callback,
                  size: "wide",
                },
              })
            );
          }, 300);

          setTimeout(() => {
            setIsLoading(false);
            setProgress(0);
          }, 300);
        }
      } else {
        const abuf = evt.target?.result as ArrayBuffer;
        const wb = XLSX.read(abuf, { type: "array" });
        const sheetNames = wb.SheetNames;

        if (sheetNames[1]) {
          const containerRows: any[][] = XLSX.utils.sheet_to_json(wb.Sheets[sheetNames[1]], {
            header: 1,
            blankrows: false,
          });

          const dataRows = containerRows.slice(1);
          //loadingTerminals
          const distinctCol0 = Array.from(new Set(dataRows.map(r => r[0])));
          //unloadingTerminals
          const distinctCol1 = Array.from(new Set(dataRows.map(r => r[1])));
          //types
          const distinctCol5 = Array.from(new Set(dataRows.map(r => r[5])));
          const grouped: Record<string, any[][]> = {};

          dataRows.forEach(row => {
            const key = `${row[0]}_${row[1]}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(row);
          });

          setTimeout(() => {
            dispatch(
              showModal({
                title: "Containers uploaded",
                description: "Uploaded containers by file",
                form: "containerupload",
                formProps: {
                  file: file?.path,
                  uploadedContainers: grouped,
                  loadingTerminals: distinctCol0,
                  unloadingTerminals: distinctCol1,
                  types: distinctCol5,
                  hasWarning: nameConflict,
                  callback,
                  size: "wide",
                },
              })
            );
          }, 300);
        }

        setTimeout(() => {
          setIsLoading(false);
          setProgress(0);
        }, 300);
      };
    }


    reader.onerror = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      console.error("File read failed");
      setIsLoading(false);
      setProgress(0);
    };

    if (file.type === "text/plain") {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const dropzone = useDropzone({
    onDrop: fileDropHandler,
    multiple: false,
    disabled: isLoading,
  });

  const rootProps = isLoading
    ? { style: { cursor: "wait" } }
    : dropzone.getRootProps({ className: "dropzone" });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl size="small" sx={{ width: "100%" }}>
          <Controller
            name={name}
            control={control}
            defaultValue={null}
            render={() => (
              <Box
                {...rootProps}
                sx={{
                  position: "relative",
                  height: height,
                  border: "3px dotted #DDD",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  bgcolor: isLoading ? "action.disabledBackground" : "inherit",
                }}
              >
                <input {...dropzone.getInputProps()} {...register(name)} />
                <Typography>{t("dropContainerFile")}</Typography>

                {isLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(255,255,255,0.8)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                      zIndex: 10,
                    }}
                  >
                    <Box sx={{ width: "100%", mb: 1 }}>
                      <LinearProgress variant="determinate" value={progress} />
                    </Box>
                    <Typography variant="caption">
                      {`${t("readingFile")}: ${progress}%`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};